<template>
  <div class="product-grid">
    <div class="product-grid__wrapper">
      <div
        v-if="showTextContainer"
        class="product-grid__text-container"
      >
        <heading
          v-if="headingText"
          :id="`section-title-${id}`"
          :text="headingText"
          :type="headingType"
          class="product-grid__heading"
          :class="headingTextAlign"
        />
        <p
          v-if="subtitleText"
          class="product-grid__subtitle"
          :class="subtitleTextAlign"
          v-text="subtitleText"
        />
        <div
          v-if="paragraphText"
          class="product-grid__paragraph"
          :class="paragraphTextAlign"
          v-html="paragraphText"
        />
        <a
          v-if="linkText"
          :href="linkUrl"
          class="product-grid__link link link--underline"
          :class="linkTextAlign"
          v-text="linkText"
        />
      </div>
      <ul
        v-if="showProductList"
        class="product-grid__list"
        :class="gridType"
      >
        <li
          v-for="(product, index) in products"
          :key="index"
          class="product-grid__item"
        >
          <component
            :is="stlEnable && product.url ? 'a' : 'div'"
            :href="product.url || ''"
            class="product-grid__item--image-wrapper"
          >
            <media-element
              v-if="!showAdditionalMedia(product)"
              :image="product.image"
              :image-desktop="product.image"
              class="product-grid__item--image main"
            />
            <media-element
              v-else
              :image="product.additional_image_mobile"
              :image-desktop="product.additional_image_desktop"
              :video="product.additional_video_mobile"
              :video-desktop="product.additional_video_desktop"
              :video-external-url="product.video_external_url"
              :video-external-desktop-url="product.video_external_desktop_url"
              :autoplay-video="product.video_autoplay"
              :loop-video="product.video_loop"
              class="product-grid__item--image"
            />
          </component>
          <div class="product-grid__item--text-wrapper">
            <heading
              v-if="product.heading_text"
              :text="product.heading_text"
              :type="product.heading_type"
              :style="{
                '--product-heading-font-family': product.heading_font_family,
                '--product-heading-font-size-desktop': product.heading_font_size_desktop + 'px',
                '--product-heading-font-size-mobile': product.heading_font_size_mobile + 'px',
                '--product-heading-font-weight': product.heading_font_weight,
                '--product-heading-text-color' : product.heading_color,
              }"
              :class="product.heading_text_align"
              class="product-grid__item--heading"
            />
            <p
              v-if="product.subtitle_text"
              class="product-grid__item--subtitle"
              :style="{
                '--product-subtitle-font-family': product.subtitle_font_family,
                '--product-subtitle-font-size-desktop': product.subtitle_font_size_desktop + 'px',
                '--product-subtitle-font-size-mobile': product.subtitle_font_size_mobile + 'px',
                '--product-subtitle-font-weight': product.subtitle_font_weight,
                '--product-subtitle-text-color' : product.subtitle_color,
              }"
              :class="product.subtitle_text_align"
              v-text="product.subtitle_text"
            />
            <div
              v-if="product.paragraph_text"
              class="product-grid__item--paragraph"
              :style="{
                '--product-paragraph-font-family': product.paragraph_font_family,
                '--product-paragraph-font-size-desktop': product.paragraph_font_size_desktop + 'px',
                '--product-paragraph-font-size-mobile': product.paragraph_font_size_mobile + 'px',
                '--product-paragraph-font-weight': product.paragraph_font_weight,
                '--product-paragraph-text-color' : product.paragraph_color,
              }"
              :class="product.paragraph_text_align"
              v-html="product.paragraph_text"
            />
            <a
              v-if="product.link_text"
              class="product-grid__item--link link link--underline"
              :style="{
                '--product-link-font-family': product.link_font_family,
                '--product-link-font-size-desktop': product.link_font_size_desktop + 'px',
                '--product-link-font-size-mobile': product.link_font_size_mobile + 'px',
                '--product-link-font-weight': product.link_font_weight,
                '--product-link-text-color' : product.link_color,
              }"
              :class="product.link_text_align"
              :href="product.link_url"
              v-text="product.link_text"
            />
          </div>
        </li>
      </ul>
      <div
        v-if="showSTLWrapper"
        class="product-grid__stl-container"
      >
        <span
          :data-gtag-select-content="stlEnable"
          :data-stl-id="stlId"
          :data-open-popup="stlPopupId"
          class="product-grid__stl shop-the-look"
          :class="[
            stlLabelPosition,
            stlMobileShow ? 'mobile-show' : 'mobile-hide'
          ]"
          v-text="stlTitle"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Section: Product Grid x3-x4
 * ------------------------------------------------------------------------------
 * - Displays a product grid.
 *
 * @param {String} id - Section Liquid's Id
 * @param {String} gridType - Type of Product Grid (3 or 4 items per row).
 *
 * @param {String} headingText - Heading text to display.
 * @param {String} headingType -Theme Settings for h1-h6 values.
 * @param {String} headingTextAlign - The alignment of heading text.
 * @param {String} subtitleText - Subtitle text to display.
 * @param {String} subtitleTextAlign - The alignment of subtitle text.
 * @param {String} paragraphText - Paragraph text to display.
 * @param {String} paragraphTextAlign - The alignment of paragraph text.
 * @param {String} linkText - Link text to display.
 * @param {String} linkTextAlign - The alignment of link text.
 * @param {String} linkUrl - The link URL.
 * @param {Boolean} stlEnable - If true, Shop the Look component will work
 * @param {Boolean} stlMobileShow - If true, Shop the Look label will be shown on mobile devices
 * @param {String} stlTitle - A Shop the Look label text
 * @param {String} stlId - A gtm id for a shop-the-look popup.
 * @param {String} stlPopupId - A unique modal id for a shop-the-look popup.
 * @param {String} stlPositionMobile - The alignment of STL label on Mobile.
 * @param {String} stlPositionDesktop - The alignment of STL label on Desktop.
 * @param {Array} products - The array of formatted product objects that are shown in the grid.
 *
 */

import { mapState } from 'pinia'

import { useScreenStore } from '~stores/screen'

import MediaElement from '~components/media-element/media-element'

import Heading from '~components/heading/heading'

export default {
  name: 'ProductGrid',

  components: {
    MediaElement,
    Heading,
  },

  props: {
    id: {
      type: String,
      default: '',
    },
    gridType: {
      type: String,
      default: '',
    },
    headingText: {
      type: String,
      default: '',
    },
    headingType: {
      type: String,
      default: '',
    },
    headingTextAlign: {
      type: String,
      default: '',
    },
    subtitleText: {
      type: String,
      default: '',
    },
    subtitleTextAlign: {
      type: String,
      default: '',
    },
    paragraphText: {
      type: String,
      default: '',
    },
    paragraphTextAlign: {
      type: String,
      default: '',
    },
    linkText: {
      type: String,
      default: '',
    },
    linkTextAlign: {
      type: String,
      default: '',
    },
    linkUrl: {
      type: String,
      default: '',
    },
    stlEnable: {
      type: Boolean,
      default: true,
    },
    stlMobileShow: {
      type: Boolean,
      default: true,
    },
    stlTitle: {
      type: String,
      default: '',
    },
    stlId: {
      type: String,
      default: '',
    },
    stlPopupId: {
      type: String,
      default: '',
    },
    stlPositionMobile: {
      type: String,
      default: '',
    },
    stlPositionDesktop: {
      type: String,
      default: '',
    },
    products: {
      type: Array,
      default:()=> ([]),
    },
  },

  computed: {

    /**
     * Map global state.
     */
    ...mapState(useScreenStore, {
      isMobile: 'mobile',
    }),

    /**
     * Calculate Shop The Look Label classes.
     * @returns {String} - class name - a combination of horizontal and vertical
     * alignment, depending on the current device (mobile/desktop)
     */
    stlLabelPosition() {
      let labelClass = ''

      if (this.isMobile) {
        labelClass = this.stlPositionMobile
      } else {
        labelClass = this.stlPositionDesktop
      }

      return labelClass
    },

    /**
     * Calculate if to show Shop The Look wrapper (as it has `min-height`).
     * @returns {Boolean}
     */
    showSTLWrapper() {
      if (!this.stlEnable || !this.stlTitle) {
        return false
      }

      return this.stlMobileShow || !this.isMobile
    },

    /**
     * Calculate if to show the text container.
     * @returns {Boolean}
     */
    showTextContainer() {
      return Boolean(this.headingText || this.subtitleText || this.paragraphText || this.linkText)
    },

    /**
     * Calculate if to show the product list based on its content.
     * @returns {Boolean}
     */
    showProductList() {
      if (!this.products.length) {
        return false
      }

      // Check if at least one product has any content
      const hasContent = this.products.some((product) => {
        if (product.image) {
          return true
        }

        if (product.heading_text || product.subtitle_text || product.paragraph_text || product.link_text) {
          return true
        }

        if (!this.isMobile) {
          return Boolean(product.external_video_url || product.additional_image_desktop || product.additional_video_desktop)
        }

        return Boolean(product.external_video_desktop_url || product.external_video_url || product.additional_image_mobile || product.additional_video_mobile)
      })

      return hasContent
    },
  },

  methods: {

    /**
     * Calculate if to show an additional media.
     *
     * @param {object} block - formatted section block.
     * @param {object|boolean} block.additional_image_mobile - formatted image object.
     * @param {object|boolean} block.additional_image_desktop - formatted image object - specific to desktop. (>= 1024px).
     * @param {object|boolean} block.additional_video_mobile - formatted video object.
     * @param {object|boolean} block.additional_video_desktop - formatted video object - specific to desktop. (>= 1024px).
     *
     * @returns {boolean}
     */
    showAdditionalMedia(block = {}) {
      console.log('BLOCK:', block.video_external_url)
      if (this.isMobile) {
        return block.video_external_url || block.additional_video_mobile || block.additional_image_mobile

      }

      return block.video_external_desktop_url || block.video_external_url || block.additional_video_desktop || block.additional_image_desktop
    },
  },
}
</script>

<style src="./product-grid.scss" lang="scss"></style>
